import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { playChime } from "@/lib/chime";
import { Bell, X, PaperPlaneTilt, CheckCircle, XCircle, ChatCircle, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { OverrideActions } from "@/components/OverrideActions";
import { fmtDateTime, normalizeDatesInText } from "@/lib/fmt";

const POLL_MS = 10000;
const FRESH_WINDOW_MS = 10 * 60 * 1000;
const SEEN_KEY = (uid) => `mfg.notif.seen.${uid || "anon"}`;
const _seenSets = new Map(); // shared across bell instances in this tab
function getSeenSet(uid) {
  if (_seenSets.has(uid)) return _seenSets.get(uid);
  let ids = [];
  try { ids = JSON.parse(localStorage.getItem(SEEN_KEY(uid)) || "[]"); } catch { ids = []; }
  const set = new Set(ids);
  _seenSets.set(uid, set);
  return set;
}
function persistSeen(uid, set) {
  try { localStorage.setItem(SEEN_KEY(uid), JSON.stringify(Array.from(set).slice(-300))); } catch { /* storage full / private mode */ }
}
const ICON_BY_TYPE = {
  load_offered: <PaperPlaneTilt size={14} weight="bold" className="text-[#0A84FF]" />,
  load_accepted: <CheckCircle size={14} weight="fill" className="text-emerald-600" />,
  load_declined: <XCircle size={14} weight="fill" className="text-red-600" />,
  load_withdrawn: <XCircle size={14} weight="fill" className="text-neutral-500" />,
  chat_message: <ChatCircle size={14} weight="fill" className="text-[#0A84FF]" />,
  promo_payout: <Sparkle size={14} weight="fill" className="text-amber-500" />,
};

/**
 * Live notification bell for any authed user (carrier, dispatcher, admin, etc).
 * Polls /api/notifications every 15s. New items pop as toasts + persist in the dropdown.
 * Click an item -> deep link + mark read.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  // "Seen" is shared across every bell instance (sidebar + mobile header) and persisted per user,
  // so a remount, tab reload or PWA resume never re-toasts alerts you've already been shown.
  const seenIdsRef = useRef(getSeenSet(user?.id));
  const primedRef = useRef(false);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const authed = !!(user && user.id);
  const unread = items.filter((n) => !n.read).length;
  const btnRef = useRef(null);
  const [popStyle, setPopStyle] = useState(null);
  const [podReview, setPodReview] = useState(0);
  const canPodReview = ["owner", "administrator", "dispatcher", "freight_agent"].includes(user?.role);
  const podReviewLink = ["owner", "administrator"].includes(user?.role) ? "/portal/command-center" : "/portal/dispatch";

  useEffect(() => {
    if (!authed || !canPodReview) return;
    const pull = () => api.get("/pod-review-queue").then((r) => setPodReview(r.data?.counts?.needs_review || 0)).catch(() => {});
    pull();
    const t = setInterval(pull, POLL_MS);
    return () => clearInterval(t);
  }, [authed, canPodReview]);

  // Position the popover in the viewport whenever it opens so the sidebar
  // context (bell at ~x=204 near the bottom of a 1080px sidebar) doesn't push
  // it off-screen horizontally OR vertically. Uses fixed positioning + clamps
  // against the viewport with an 8px gutter. Recomputes on resize/scroll.
  useEffect(() => {
    if (!open) { setPopStyle(null); return; }
    const compute = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gutter = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(380, vw - 2 * gutter);
      // Horizontal: right-align with the button, clamp to viewport
      let left = rect.right - width;
      if (left < gutter) left = gutter;
      if (left + width > vw - gutter) left = vw - gutter - width;
      // Vertical: try to open below the button; if not enough room, flip above.
      // Cap max-height to whichever side has more space so the list always fits.
      const spaceBelow = vh - rect.bottom - gutter;
      const spaceAbove = rect.top - gutter;
      const preferBelow = spaceBelow >= 260 || spaceBelow >= spaceAbove;
      const base = {
        position: "fixed",
        left,
        width,
        display: "flex",
        flexDirection: "column",
      };
      if (preferBelow) {
        base.top = rect.bottom + gutter;
        base.maxHeight = Math.max(200, Math.min(520, spaceBelow));
      } else {
        // Open ABOVE the button — anchor the popover's BOTTOM edge 8px above
        // the bell so it looks visually docked to the button instead of
        // stretching all the way up to the top of the viewport.
        base.bottom = Math.max(gutter, vh - rect.top + gutter);
        base.maxHeight = Math.max(200, Math.min(520, spaceAbove - gutter));
      }
      setPopStyle(base);
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const fetchNotifs = useCallback(async () => {
    if (!authed) return;
    try {
      const { data } = await api.get("/notifications?limit=40");
      setItems(data);
      const seen = seenIdsRef.current;
      if (!primedRef.current) {
        // First load: prime "seen" set silently — don't toast historical events
        data.forEach((n) => seen.add(n.id));
        primedRef.current = true;
        persistSeen(user?.id, seen);
        return;
      }
      // Only genuinely new alerts pop: not seen by any bell instance, and created in the last few
      // minutes (older unread ones were already delivered by push / an earlier session — badge only).
      const cutoff = Date.now() - FRESH_WINDOW_MS;
      const fresh = data.filter((n) => !seen.has(n.id) && (!n.created_at || new Date(n.created_at).getTime() >= cutoff));
      data.forEach((n) => seen.add(n.id));
      persistSeen(user?.id, seen);
      if (fresh.length) playChime();
      fresh.forEach((n) => toast(normalizeDatesInText(n.title), { id: `notif-${n.id}`, description: normalizeDatesInText(n.message), duration: 1000 }));
    } catch { /* silent */ }
  }, [authed, user?.id]);

  useEffect(() => {
    if (!authed) return;
    fetchNotifs();
    const t = setInterval(fetchNotifs, POLL_MS);
    // Push arrived while the app is on screen → refresh instantly (one toast + one chime, no OS banner).
    const onSw = (e) => {
      if (e.data?.type === "mfg-push") fetchNotifs();
      if (e.data?.type === "mfg-push-shown" && e.data.id) { seenIdsRef.current.add(e.data.id); persistSeen(user?.id, seenIdsRef.current); }
    };
    navigator.serviceWorker?.addEventListener("message", onSw);
    const onVisible = () => { if (document.visibilityState === "visible") fetchNotifs(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      navigator.serviceWorker?.removeEventListener("message", onSw);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [authed, fetchNotifs, user?.id]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!authed) return null;

  const clickNotif = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, read: true } : x));
    setOpen(false);
    if (n.link) nav(n.link);
  };

  const ackNotif = async (n, e) => {
    e.stopPropagation();
    try {
      await api.post(`/notifications/${n.id}/ack`);
      setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, read: true, acknowledged: true } : x));
      toast.success("Dispatch has been notified you saw the update");
    } catch { toast.error("Couldn't acknowledge — try again"); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); } catch { /* ignore */ }
    setItems((cur) => cur.map((x) => ({ ...x, read: true })));
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="notif-bell">
      <button ref={btnRef} onClick={() => setOpen((v) => !v)} data-testid="notif-bell-toggle"
        className="relative w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center transition-colors"
        aria-label="Notifications">
        <Bell size={18} weight={unread > 0 || podReview > 0 ? "fill" : "regular"} className={unread > 0 || podReview > 0 ? "text-amber-400" : ""} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold grid place-items-center border-2 border-black" data-testid="notif-bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {podReview > 0 && (
          <span title={`${podReview} POD${podReview === 1 ? "" : "s"} waiting for manual review`}
            className="absolute -bottom-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[9px] font-black grid place-items-center border-2 border-black" data-testid="notif-bell-pod-badge">
            {podReview > 99 ? "99+" : podReview}
          </span>
        )}
      </button>

      {open && popStyle && createPortal(
        <div ref={popRef} style={{ ...popStyle, zIndex: 1000 }}
          className="bg-white text-black rounded-2xl shadow-2xl border border-black/10 z-[100] overflow-hidden"
          data-testid="notif-bell-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
            <div className="font-display font-bold">Notifications</div>
            <div className="flex items-center gap-2">
              {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-[11px] font-semibold text-[#0A84FF] hover:underline">Mark all read</button>}
              <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-black"><X size={14} /></button>
            </div>
          </div>
          {podReview > 0 && (
            <button onClick={() => { setOpen(false); nav(podReviewLink); }} data-testid="notif-pod-review-row"
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-left hover:bg-amber-100 transition-colors">
              <div>
                <div className="text-xs font-bold text-amber-900">{podReview} signed POD{podReview === 1 ? "" : "s"} waiting for your review</div>
                <div className="text-[11px] text-amber-800/80">AI couldn't fully verify — approve to send the remittance packet</div>
              </div>
              <span className="text-[11px] font-black text-amber-900 whitespace-nowrap">Open queue →</span>
            </button>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-500 flex flex-col items-center gap-2">
                <CircleNotch size={20} weight="bold" className="text-neutral-300" />
                You're all caught up.
              </div>
            ) : (
              items.slice(0, 30).map((n) => (
                <button key={n.id} onClick={() => clickNotif(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-black/5 flex items-start gap-3 hover:bg-neutral-50 transition-colors ${!n.read ? "bg-[#0A84FF]/5" : ""}`}>
                  <div className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center shrink-0 mt-0.5">
                    {ICON_BY_TYPE[n.type] || <Bell size={14} className="text-neutral-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${!n.read ? "text-black" : "text-neutral-600"}`}>{normalizeDatesInText(n.title)}</div>
                    <div className="text-xs text-neutral-500 line-clamp-2">{normalizeDatesInText(n.message)}</div>
                    <div className="text-[10px] text-neutral-400 mt-1">{fmtDateTime(n.created_at)}</div>
                    {n.type === "override_expiring" && n.meta?.carrier_id && ["owner", "administrator"].includes(user?.role) && (
                      <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                        <OverrideActions carrierId={n.meta.carrier_id} testId={`notif-override-${n.id}`}
                          onDone={async () => { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, read: true } : x)); }} />
                      </div>
                    )}
                    {n.type === "load_updated" && (
                      n.acknowledged ? (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid={`notif-acked-${n.id}`}>
                          <CheckCircle size={12} weight="fill" /> Got it — dispatch notified
                        </div>
                      ) : (
                        <span role="button" tabIndex={0} onClick={(e) => ackNotif(n, e)}
                          onKeyDown={(e) => { if (e.key === "Enter") ackNotif(n, e); }}
                          data-testid={`notif-ack-btn-${n.id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1 transition-colors cursor-pointer">
                          <CheckCircle size={12} weight="fill" /> Got it
                        </span>
                      )
                    )}
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-[#0A84FF] shrink-0 mt-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      , document.body)}
    </div>
  );
}
